import React, { useState } from 'react';
import { Table, Icon, Label, Message, Header } from 'semantic-ui-react';
import { Button } from '~/components';
import BidForm from '~/features/projectBidding/investor/BidForm';
import BidHistory from '~/features/projectBidding/shared/BidHistory';
import { formatDollars, formatNumber, offtakeBidDecimals } from '~/utils';
import { normalizeToPerWdc } from '~/utils/calculators/costsFns';
import { sizeKwdc } from '~/utils/calculators/system';
import { Unit } from '~/constants';
import { ProjectBid } from '~/types/project';
import { usePermissions } from '~/requests/permissions/usePermissions';

interface InvestorProjectRowProps {
    project: any;
    index: number;
    hasStorage: boolean;
    hasPv: boolean;
    fetchPortfolio: () => void;
}

const InvestorProjectRow: React.FC<InvestorProjectRowProps> = ({
    project,
    index,
    hasStorage,
    hasPv,
    fetchPortfolio
}) => {
    const { permissions } = usePermissions();
    const [isExpanded, setIsExpanded] = useState(false);

    const getBidStatus = (project: any, format: 'unit' | 'total') => {
        if (project.status === 'DECLINED_TO_BID') {
            return (
                <Label style={{ backgroundColor: '#f8d7da', color: '#721c24' }}>
                    Declined to bid
                </Label>
            );
        }

        const bid = project.projectBids?.find((bid: any) => bid.bidStatus === 'PENDING');
        if (bid) {
            if (!bid) return null;

            const projectSizeKwdc = sizeKwdc(project);

            if (project?.pricingType === 'OFFTAKE') {
                return `$${formatNumber(bid.amount, offtakeBidDecimals(project?.contractType))}`;
            }

            let amount: number;
            if (bid.unit === Unit.WDC) {
                if (format === 'total') {
                    amount = bid.amount * projectSizeKwdc * 1000;
                } else {
                    amount = bid.amount;
                }
            } else if (bid.unit === Unit.TOTAL) {
                if (format === 'total') {
                    amount = bid.amount;
                } else {
                    amount = normalizeToPerWdc(projectSizeKwdc, { amount: bid.amount, unit: Unit.TOTAL } as ProjectBid);
                }
            } else {
                amount = bid.amount; // Default or error case
            }

            return formatDollars(amount, { dec: format === 'unit' ? 3 : 0 });
        };

        return <Label>Awaiting bid</Label>;
    };

    const handleRowClick = () => {
        setIsExpanded(!isExpanded);
    };

    const hasPendingBid = !!project.projectBids?.find((bid: any) => bid.bidStatus === 'PENDING');
    const isDeclined = project.status === 'DECLINED_TO_BID';

    return (
        <>
            <Table.Row style={{ cursor: 'pointer' }} onClick={handleRowClick}>
                <Table.Cell>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <Icon name='dropdown' style={{ marginRight: '8px', height: 'auto' }} className={'row-dropdown' + (isExpanded ? ' open' : '')} />
                        <span style={{ fontWeight: '700' }}>{project.name}</span>
                    </div>
                </Table.Cell>
                {/* {hasPv ? (
                    <Table.Cell textAlign="right">
                        {calculateTotalPv(project)} MW
                    </Table.Cell>
                ) : null}
                {hasStorage ? (
                    <Table.Cell textAlign="right">
                        {getStorageDisplay(project)}
                    </Table.Cell>
                ) : null} */}
                <Table.Cell textAlign="right">
                    {getBidStatus(project, 'unit')}
                </Table.Cell>
                <Table.Cell textAlign="right">
                    {getBidStatus(project, 'total')}
                </Table.Cell>
                <Table.Cell textAlign="right">
                    {!isDeclined && (
                        <Button
                            primary
                            size="small"
                            onClick={(e: React.MouseEvent) => {
                                e.stopPropagation();
                                setIsExpanded(true);
                            }}
                        >
                            {hasPendingBid ? 'Update bid' : 'Bid now'}
                        </Button>
                    )}
                </Table.Cell>
            </Table.Row>

            {isExpanded && (
                <Table.Row>
                    <Table.Cell colSpan={4} style={{ padding: '20px', backgroundColor: '#f1f1f1' }}>
                        {project.status === 'DECLINED_TO_BID' ? (
                            <Message negative compact style={{ width: '100%', background: 'white' }}>
                                <Message.Header style={{ marginBottom: '8px' }}>You have declined to bid on this project.</Message.Header>
                                <p>If you would like to resubmit a bid, please contact your Conductor account rep.</p>
                            </Message>
                        ) : (
                            <BidForm
                                project={project}
                                id={project.id}
                                fetchProject={fetchPortfolio}
                                projectState={{ isEpcOperating: false }}
                            />
                        )}

                        {permissions?.isAdmin && (
                            <div style={{ marginTop: '20px' }}>
                                <Header as="h5">Bid History</Header>
                                <BidHistory project={project} />
                            </div>
                        )}
                    </Table.Cell>
                </Table.Row>
            )}
        </>
    );
};

export default InvestorProjectRow;